import React, { useRef, useState } from 'react';
import {
    Box,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Heading,
    Input,
    Text
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'ts/common/components/gallery';
import { useBreakpoint, useTranslate } from 'ts/common/hooks';
import { isContractError } from 'client_react/common/hooks/useContract';
import { useClientContract } from 'client_react/contract/App';
import { ROUTE } from 'client_react/contract/common';

const EmailVerificationPage: React.FC = () => {
    const [isInvalidEmail, setIsInvalidEmail] = useState(false);
    const emailInputRef = useRef<HTMLInputElement>(null);
    const t = useTranslate('contract.emailVerificationPage');
    const navigate = useNavigate();
    const {
        contract,
        getContract,
        isPending: isGettingContract,
        setContractUserEmail
    } = useClientContract();
    const isLargeScreen = useBreakpoint();

    const submitEmail = async () => {
        const email = emailInputRef.current?.value;

        if (contract && email) {
            const contractResponse = await getContract({
                search: { email },
                url: `${contract.publicId}`
            });

            if (!contractResponse || isContractError(contractResponse)) {
                setIsInvalidEmail(true);
                emailInputRef.current?.focus();
            } else {
                setContractUserEmail(email);
                navigate(`/${ROUTE.CONTRACT}/${contract.publicId}/${ROUTE.VIEW}`);
            }
        }
    };

    return (
        <Flex
            alignItems="center"
            aria-labelledby="verification-heading"
            flexDirection="column"
            height="268px"
            justifyContent="center"
            as="main"
        >
            <Heading as="h1" id="verification-heading" size="lg" textAlign="center">
                {t('header')}
            </Heading>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    submitEmail();
                }}
                aria-labelledby="verification-heading"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    maxWidth: '350px'
                }}
            >
                <FormControl
                    height="80px"
                    isInvalid={isInvalidEmail}
                    isRequired
                    maxWidth="450px"
                    onChange={() => {
                        if (isInvalidEmail) {
                            setIsInvalidEmail(false);
                        }
                    }}
                >
                    <FormLabel htmlFor="email-input">{t('email')}</FormLabel>
                    <Box display="flex">
                        <Input
                            id="email-input"
                            type="email"
                            ref={emailInputRef}
                            aria-errormessage={isInvalidEmail ? 'email-error-message' : undefined}
                            aria-invalid={isInvalidEmail}
                        />
                    </Box>
                    <FormErrorMessage id="email-error-message" aria-live="assertive">
                        <Text color="error">{t('emailError')}</Text>
                    </FormErrorMessage>
                </FormControl>
                <Button
                    type="submit"
                    width={isLargeScreen ? '200px' : '100%'}
                    isLoading={isGettingContract}
                    marginTop="16px"
                    text={t('viewContract')}
                    aria-busy={isGettingContract}
                />
            </form>
        </Flex>
    );
};

export default EmailVerificationPage;
